import cx from 'classnames'

import { type SanityBlock } from '@data/sanity/queries/types/blocks'

import Accordions from '@blocks/accordions'
import AccountAddressDetails from '@blocks/account/account-address-details'
import AccountDetails from '@blocks/account/account-details'
import AccountHeader from '@blocks/account/account-header'
import AccountOrderList from '@blocks/account/account-order-list'
import BlogPostCard from '@blocks/blog/blog-post-card'
import ProductCarousel from '@blocks/shop/product-carousel'
import ContentCarousel from '@blocks/content-carousel'
import CookieDeclaration from '@blocks/cookiebot/cookie-declaration'
import Freeform from '@blocks/freeform'
import LinkBlock from '@blocks/link-block'
import LoginForm from '@blocks/account/login-form'
import Newsletter from '@blocks/newsletter'
import PasswordRecoveryForm from '@blocks/account/password-recovery-form'
import ProductCard from '@blocks/shop/product-card'
import SignupForm from '@blocks/account/signup-form'
import TeamMemberCard from '@blocks/team-member-card'
import Photo from '@components/photo'
import Video from '@components/video'

interface GridBlockProps {
  block: SanityBlock
  className?: string
}

const GridBlock = ({ block, className }: GridBlockProps) => {
  switch (block._type) {
    case 'accordions': {
      const { items } = block

      return <Accordions items={items} className={className} />
    }

    case 'accountAddressDetails': {
      const { active, accountAddressDetailsStrings, addressFormStrings } = block

      if (!active) {
        return null
      }

      return (
        <AccountAddressDetails
          accountAddressDetailsStrings={accountAddressDetailsStrings}
          addressFormStrings={addressFormStrings}
          className={className}
        />
      )
    }

    case 'accountDetails': {
      const { active, accountDetailsStrings } = block

      if (!active) {
        return null
      }

      return (
        <AccountDetails
          accountDetailsStrings={accountDetailsStrings}
          className={className}
        />
      )
    }

    case 'accountHeader': {
      const { active, accountHeaderStrings } = block

      if (!active) {
        return null
      }

      return (
        <AccountHeader
          accountHeaderStrings={accountHeaderStrings}
          className={className}
        />
      )
    }

    case 'accountOrderList': {
      const { active, accountOrderListStrings } = block

      if (!active) {
        return null
      }

      return (
        <AccountOrderList
          accountOrderListStrings={accountOrderListStrings}
          className={cx('mb-16', className)}
        />
      )
    }

    case 'blogPostCard': {
      const { post } = block

      return <BlogPostCard post={post} className={className} />
    }

    case 'productCarousel': {
      const { title, products } = block

      return (
        <ProductCarousel
          title={title}
          products={products}
          className={className}
        />
      )
    }

    case 'contentCarousel': {
      const { items } = block

      return <ContentCarousel items={items} className={className} />
    }

    case 'cookieDeclaration': {
      const { active } = block

      if (!active) {
        return null
      }

      return <CookieDeclaration />
    }

    case 'figure': {
      return (
        <Photo image={block} imageClassName="w-full" className={className} />
      )
    }

    case 'freeform': {
      const { content, textAlign, maxWidth } = block

      return (
        <Freeform
          content={content}
          textAlign={textAlign}
          maxWidth={maxWidth}
          className={className}
        />
      )
    }

    case 'iframe': {
      const { title, url, width, height } = block

      return (
        <iframe
          title={title}
          src={url}
          width={width}
          height={height}
          className={cx({ 'w-full': !width }, className)}
        />
      )
    }

    case 'linkBlock': {
      const {
        link,
        collectionLinkContext,
        parentCollection,
        queryString,
        url,
        text,
        textColor,
        backgroundType,
        mobilePhoto,
        photo,
        muxVideo,
      } = block

      return (
        <LinkBlock
          link={link}
          collectionLinkContext={collectionLinkContext}
          parentCollection={parentCollection}
          queryString={queryString}
          url={url}
          text={text}
          textColor={textColor}
          backgroundType={backgroundType}
          mobilePhoto={mobilePhoto}
          photo={photo}
          muxVideo={muxVideo}
          className={className}
        />
      )
    }

    case 'loginForm': {
      const { active, loginFormStrings } = block

      if (!active) {
        return null
      }

      return (
        <LoginForm loginFormStrings={loginFormStrings} className={className} />
      )
    }

    case 'newsletter': {
      const { _key, service, listId, submit, successMsg, errorMsg, terms } =
        block

      return (
        <Newsletter
          id={_key}
          service={service}
          listId={listId}
          submit={submit}
          successMsg={successMsg}
          errorMsg={errorMsg}
          terms={terms}
          className={className}
        />
      )
    }

    case 'passwordRecoveryForm': {
      const { active, passwordRecoveryFormStrings } = block

      if (!active) {
        return null
      }

      return (
        <PasswordRecoveryForm
          passwordRecoveryFormStrings={passwordRecoveryFormStrings}
          className={className}
        />
      )
    }

    case 'predefinedContentBlock': {
      const { contentBlocks } = block

      return (
        <>
          {contentBlocks?.map((contentBlock) => (
            <GridBlock
              key={contentBlock._key}
              block={contentBlock}
              className={className}
            />
          ))}
        </>
      )
    }

    case 'productCard': {
      const { product } = block

      return (
        <ProductCard
          product={product}
          showThumbnails
          showOption
          showPrice
          className={className}
        />
      )
    }

    case 'signupForm': {
      const { active, signupFormStrings } = block

      if (!active) {
        return null
      }

      return (
        <SignupForm
          signupFormStrings={signupFormStrings}
          className={className}
        />
      )
    }

    case 'teamMemberCard': {
      const { teamMember } = block

      return <TeamMemberCard teamMember={teamMember} className={className} />
    }

    case 'video': {
      const { type, muxVideo, settings, aspectRatio } = block

      return (
        <Video
          type={type}
          muxVideo={muxVideo}
          settings={settings}
          aspectRatio={aspectRatio}
          className={className}
        />
      )
    }
  }
}

export default GridBlock
